import type {
  AuthMe,
  Card,
  CardStatus,
  CardType,
  ChatMessage,
  ExportRecord,
  GenerationOptions,
  HealthCheck,
  Job,
  MediaAsset,
  OcclusionShape,
  Preflight,
  Project,
  ProjectDetail,
  Settings,
  Source,
  User,
  ApiErrorShape,
} from "./types";

const BASE = "/api";

/** Carries the backend's structured error so the UI can show the hint. */
export class ApiError extends Error {
  code: string;
  hint?: string | null;
  detail?: Record<string, unknown>;
  status: number;

  constructor(status: number, shape: ApiErrorShape) {
    super(shape.message);
    this.name = "ApiError";
    this.status = status;
    this.code = shape.code;
    this.hint = shape.hint;
    this.detail = shape.detail;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      // The session cookie is httpOnly and same-origin (see vite.config.ts's
      // dev proxy and main.py's single-process prod serving) - "include"
      // makes fetch actually send and accept it either way.
      credentials: "include",
      headers:
        init?.body instanceof FormData
          ? init?.headers
          : { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError(0, {
      code: "network_error",
      message: "Could not reach the backend.",
      hint: "Is the API server running on port 8000?",
    });
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? safeJson(text) : null;

  if (!response.ok) {
    const shape =
      payload && typeof payload === "object" && "error" in payload
        ? (payload as { error: ApiErrorShape }).error
        : { code: "http_error", message: `Request failed (${response.status}).` };
    throw new ApiError(response.status, shape);
  }
  return payload as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const qs = (params: Record<string, string | string[] | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) value.forEach((v) => search.append(key, v));
    else search.set(key, value);
  }
  const out = search.toString();
  return out ? `?${out}` : "";
};

export const api = {
  // -- auth ---------------------------------------------------------------
  signup: (email: string, password: string) =>
    request<User>("/auth/signup", { method: "POST", body: JSON.stringify({ email, password }) }),
  login: (email: string, password: string) =>
    request<User>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  me: () => request<AuthMe>("/auth/me"),
  logout: () => request<void>("/auth/logout", { method: "POST" }),

  // -- billing ------------------------------------------------------------
  checkout: (plan: "premium" | "pro") =>
    request<{ url: string }>("/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ plan }),
    }),
  billingPortal: () => request<{ url: string }>("/billing/portal", { method: "POST" }),

  // -- settings ---------------------------------------------------------
  settings: () => request<Settings>("/settings"),
  health: () => request<HealthCheck>("/settings/health"),

  // -- projects ---------------------------------------------------------
  listProjects: () => request<Project[]>("/projects"),
  getProject: (id: string) => request<ProjectDetail>(`/projects/${id}`),
  createProject: (body: {
    name: string;
    subject?: string;
    course?: string;
    deck_name?: string;
    tags?: string[];
  }) => request<Project>("/projects", { method: "POST", body: JSON.stringify(body) }),
  updateProject: (
    id: string,
    body: Partial<{
      name: string;
      subject: string;
      course: string;
      deck_name: string;
      tags: string[];
      options: GenerationOptions;
    }>,
  ) => request<Project>(`/projects/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteProject: (id: string) => request<void>(`/projects/${id}`, { method: "DELETE" }),

  // -- sources ----------------------------------------------------------
  listSources: (projectId: string) => request<Source[]>(`/projects/${projectId}/sources`),
  uploadSources: (projectId: string, files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f, f.name));
    return request<{ job_id: string; sources: Source[]; skipped: { filename: string; reason: string }[] }>(
      `/projects/${projectId}/sources`,
      { method: "POST", body: form },
    );
  },
  reprocessSource: (projectId: string, sourceId: string) =>
    request<{ job_id: string }>(`/projects/${projectId}/sources/${sourceId}/reprocess`, {
      method: "POST",
    }),
  deleteSource: (projectId: string, sourceId: string) =>
    request<void>(`/projects/${projectId}/sources/${sourceId}`, { method: "DELETE" }),

  // -- generation -------------------------------------------------------
  generate: (projectId: string, options?: GenerationOptions) =>
    request<{ job_id: string; warning: string | null }>(`/projects/${projectId}/generate`, {
      method: "POST",
      body: JSON.stringify({ options: options ?? null }),
    }),
  getJob: (jobId: string) => request<Job>(`/jobs/${jobId}`),
  listJobs: (projectId: string) => request<Job[]>(`/projects/${projectId}/jobs`),

  // -- cards ------------------------------------------------------------
  listCards: (
    projectId: string,
    opts: { status?: CardStatus[]; card_type?: CardType; search?: string; order?: string } = {},
  ) =>
    request<Card[]>(
      `/projects/${projectId}/cards${qs({
        status: opts.status,
        card_type: opts.card_type,
        search: opts.search || undefined,
        order: opts.order,
      })}`,
    ),
  getCard: (cardId: string) => request<Card>(`/cards/${cardId}`),
  similar: (cardId: string) =>
    request<{ card_id: string; score: number; preview: string }[]>(`/cards/${cardId}/similar`),
  updateCard: (
    cardId: string,
    body: Partial<{
      type: CardType;
      front: string;
      back: string;
      cloze_text: string;
      extra: string;
      header: string;
      tags: string[];
      topic: string;
      difficulty: number;
      status: CardStatus;
      occlusion_shapes: OcclusionShape[];
    }>,
  ) => request<Card>(`/cards/${cardId}`, { method: "PATCH", body: JSON.stringify(body) }),
  regenerateCard: (cardId: string, instruction: string, targetType?: CardType) =>
    request<Card>(`/cards/${cardId}/regenerate`, {
      method: "POST",
      body: JSON.stringify({ instruction, target_type: targetType ?? null }),
    }),
  deleteCard: (cardId: string) => request<void>(`/cards/${cardId}`, { method: "DELETE" }),
  bulkStatus: (projectId: string, cardIds: string[], status: CardStatus) =>
    request<{ updated: number; blocked: { card_id: string; reason: string }[] }>(
      `/projects/${projectId}/cards/bulk-status`,
      { method: "POST", body: JSON.stringify({ card_ids: cardIds, status }) },
    ),
  acceptAllPassing: (projectId: string) =>
    request<{ accepted: number }>(`/projects/${projectId}/cards/accept-all-passing`, {
      method: "POST",
    }),

  // -- media ------------------------------------------------------------
  listMedia: (projectId: string) => request<MediaAsset[]>(`/projects/${projectId}/media`),
  mediaUrl: (mediaId: string) => `${BASE}/media/${mediaId}/file`,

  // -- refinement -------------------------------------------------------
  chatHistory: (projectId: string) => request<ChatMessage[]>(`/projects/${projectId}/chat`),
  chat: (projectId: string, message: string, cardIds?: string[]) =>
    request<{ reply: string; actions: Record<string, any>[]; changed_card_ids: string[] }>(
      `/projects/${projectId}/chat`,
      { method: "POST", body: JSON.stringify({ message, card_ids: cardIds ?? null }) },
    ),
  clearChat: (projectId: string) =>
    request<void>(`/projects/${projectId}/chat`, { method: "DELETE" }),

  // -- export -----------------------------------------------------------
  preflight: (projectId: string) => request<Preflight>(`/projects/${projectId}/export/preflight`),
  exportDeck: (
    projectId: string,
    body: {
      deck_name?: string;
      include_statuses?: CardStatus[];
      organise_into_subdecks?: boolean;
      extra_tags?: string[];
    },
  ) =>
    request<ExportRecord>(`/projects/${projectId}/export`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listExports: (projectId: string) => request<ExportRecord[]>(`/projects/${projectId}/exports`),
};

export const jobStreamUrl = (jobId: string) => `${BASE}/jobs/${jobId}/stream`;
